import React, { useMemo, useState } from 'react';
import type { Risk, RAG } from '../types/risk.type';
import RiskEditorModal from './RiskEditorModal';
import { dateCmp, strCmp, numCmp } from 'src/utils/sort';
import type { CreateRiskInput, UpdateRiskPatch } from '../api/risks.api';
import { toProjectDateOnly } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

// type SortKey = 'id' | 'score' | 'category' | 'createdAt' | 'createdBy' | 'owner' | 'rag';
type SortKey =
    | 'createdAt'
    | 'updatedAt'
    | 'status'
    | 'dueDate'
    | 'rag'
    | 'score'
    | 'category'
    | 'title'
    | 'owner'
    | 'createdBy'
    | 'id';
type SortDir = 'asc' | 'desc';

const ragOrder = (rag: RAG | null | undefined) => (rag === 'R' ? 3 : rag === 'A' ? 2 : rag === 'G' ? 1 : 0);
const ragPill = (rag?: RAG | null) =>
    rag === 'R'
        ? 'bg-rose-100 text-rose-700'
        : rag === 'A'
          ? 'bg-amber-100 text-amber-700'
          : rag === 'G'
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-gray-100 text-gray-700';

export default function RiskListForm({
    risks,
    projectId,
    canEdit = false,
    includeClosed,
    setIncludeClosed,
    filterCategory,
    setFilterCategory,
    clearFilters,
    reloadRisks,
    memberMap,
    currentUserId,
    userNameMap,
    onCreate,
    onUpdate,
    onDelete,
}: {
    risks: Risk[];
    projectId: string;
    canEdit?: boolean;
    includeClosed: boolean;
    setIncludeClosed: React.Dispatch<React.SetStateAction<boolean>>;
    filterCategory: string | null;
    setFilterCategory: React.Dispatch<React.SetStateAction<string | null>>;
    clearFilters?: () => void;
    reloadRisks: () => void;
    memberMap: Map<string, { name: string; email: string }>;
    currentUserId?: string;
    userNameMap?: Record<string, string>;
    onCreate: (input: CreateRiskInput) => Promise<Risk>;
    onUpdate: (riskId: string, patch: UpdateRiskPatch) => Promise<Risk>;
    onDelete: (riskId: string) => Promise<any>;
}) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    const [sortKey, setSortKey] = useState<SortKey>('createdAt');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [q, setQ] = useState('');
    const [view, setView] = useState<'ALL' | 'OWNED_ME' | 'CREATED_ME'>('ALL');

    const [detailOpen, setDetailOpen] = useState(false);
    const [editing, setEditing] = useState<Risk | null>(null);

    // const sorted = useMemo(() => {
    //     const arr = [...risks];
    //     arr.sort((a, b) => {
    //         let cmp = 0;
    //         switch (sortKey) {
    //             case 'id':
    //                 cmp = strCmp(a.id, b.id);
    //                 break;
    //             case 'score':
    //                 cmp = numCmp(a.score ?? null, b.score ?? null);
    //                 break;
    //             case 'category':
    //                 cmp = strCmp(a.category ?? null, b.category ?? null);
    //                 break;
    //             case 'createdAt':
    //                 cmp = dateCmp(a.createdAt ?? null, b.createdAt ?? null);
    //                 break;
    //             case 'createdBy':
    //                 cmp = strCmp(a.createdByUserId ?? null, b.createdByUserId ?? null);
    //                 break;
    //             case 'owner':
    //                 cmp = strCmp(a.ownerUserId ?? null, b.ownerUserId ?? null);
    //                 break;
    //             case 'rag': {
    //                 const va = ragOrder(a.rag || null);
    //                 const vb = ragOrder(b.rag || null);
    //                 cmp = numCmp(va, vb);
    //                 break;
    //             }
    //         }
    //         return sortDir === 'asc' ? cmp : -cmp;
    //     });
    //     return arr;
    // }, [risks, sortKey, sortDir]);

    const filtered = useMemo(() => {
        let arr = [...risks];

        // view filter (TaskList와 동일한 패턴)
        if (view === 'OWNED_ME' && currentUserId) arr = arr.filter((r) => r.ownerUserId === currentUserId);
        else if (view === 'CREATED_ME' && currentUserId) arr = arr.filter((r) => r.createdByUserId === currentUserId);

        // 검색
        const s = q.trim().toLowerCase();
        if (s) {
            arr = arr.filter((r) => {
                const hay = [
                    r.title,
                    r.category,
                    r.description,
                    r.mitigationPlan,
                    r.contingencyPlan,
                    r.weeklyNote,
                    r.closedNote,
                    String(r.ownerUserId ?? ''),
                    String(r.createdByUserId ?? ''),
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                return hay.includes(s);
            });
        }

        return arr;
    }, [risks, q, view, currentUserId]);

    const sorted = useMemo(() => {
        const arr = [...filtered];
        arr.sort((a, b) => {
            let cmp = 0;
            switch (sortKey) {
                case 'createdAt':
                    cmp = dateCmp(a.createdAt ?? null, b.createdAt ?? null);
                    break;
                case 'updatedAt':
                    cmp = dateCmp(a.updatedAt ?? a.createdAt ?? null, b.updatedAt ?? b.createdAt ?? null);
                    break;
                case 'status': {
                    // OPEN 우선(=높은 우선순위) / CLOSED 뒤
                    const rank = (s?: string | null) => (s === 'OPEN' ? 1 : 0);
                    cmp = numCmp(rank(a.status ?? null), rank(b.status ?? null));
                    break;
                }
                case 'dueDate':
                    cmp = dateCmp(a.dueDate ?? null, b.dueDate ?? null);
                    break;
                case 'rag':
                    cmp = numCmp(ragOrder(a.rag ?? null), ragOrder(b.rag ?? null));
                    break;
                case 'score':
                    cmp = numCmp(a.score ?? null, b.score ?? null);
                    break;
                case 'category':
                    cmp = strCmp(a.category ?? null, b.category ?? null);
                    break;
                case 'title':
                    cmp = strCmp(a.title ?? null, b.title ?? null);
                    break;
                case 'owner':
                    cmp = strCmp(a.ownerUserId ?? null, b.ownerUserId ?? null);
                    break;
                case 'createdBy':
                    cmp = strCmp(a.createdByUserId ?? null, b.createdByUserId ?? null);
                    break;
                case 'id':
                default:
                    cmp = strCmp(a.id, b.id);
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return arr;
    }, [filtered, sortKey, sortDir]);

    const renderUserCell = (userId?: string | null) => {
        if (!userId) return <span className="text-gray-400">—</span>;

        const u = memberMap.get(userId);
        if (!u) return <span className="text-gray-700">{userId.slice(0, 8)}</span>;

        const name = (u.name ?? '').trim();
        const email = (u.email ?? '').trim();

        if (!name && !email) return <span className="text-gray-400">—</span>;

        return (
            <div className="leading-tight">
                {name ? <div className="text-sm font-medium text-gray-900">{name}</div> : null}
                {email ? <div className="text-xs text-gray-500">{email}</div> : null}
            </div>
        );
    };

    const openNew = () => {
        setEditing(null);
        setDetailOpen(true);
    };

    const openEdit = (r: Risk) => {
        setEditing(r);
        setDetailOpen(true);
    };

    return (
        <div className="w-full space-y-4">
            {/* 상단: 액션/정렬 */}
            {/* <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="text-xs text-gray-500">정렬:</div>
                    <select
                        className="text-xs rounded border px-2 py-1"
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value as SortKey)}
                    >
                        <option value="id">ID</option>
                        <option value="score">Score</option>
                        <option value="category">카테고리</option>
                        <option value="createdAt">생성일</option>
                        <option value="owner">생성자</option>
                        <option value="rag">RAG</option>
                    </select>
                    <button
                        className="text-xs rounded border px-2 py-1 hover:bg-gray-50"
                        onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                        title="정렬 방향 전환"
                    >
                        {sortDir === 'asc' ? '▲ asc' : '▼ desc'}
                    </button>
                    <label className="ml-4 inline-flex items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            className="accent-gray-900"
                            checked={includeClosed}
                            onChange={(e) => setIncludeClosed(e.target.checked)}
                        />
                        <span>Show Closed</span>
                    </label>

                    {filterCategory && (
                        <span className="ml-3 inline-flex items-center gap-2 text-xs rounded-full border px-2 py-0.5">
                            Category: <b>{filterCategory}</b>
                            <button className="opacity-70 hover:opacity-100" onClick={() => setFilterCategory(null)}>
                                ×
                            </button>
                        </span>
                    )}

                    {(filterCategory || includeClosed) && (
                        <button className="ml-2 text-xs underline" onClick={clearFilters}>
                            Clear filters
                        </button>
                    )}

                    <span className="ml-2 text-xs text-gray-500">총 {sorted.length}건</span>
                </div>

                <div>
                    <button
                        className="text-xs rounded-lg border px-2.5 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-50"
                        onClick={openNew}
                        disabled={!canEdit}
                    >
                        + New Risk
                    </button>
                </div>
            </div> */}

            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    <input
                        className="w-64 rounded border px-2 py-1 text-sm"
                        placeholder="Search risks…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />

                    {/* view filter (TaskList 스타일) */}
                    <div className="inline-flex rounded-lg border overflow-hidden text-xs">
                        <button
                            className={`px-2.5 py-1 ${view === 'ALL' ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'}`}
                            onClick={() => setView('ALL')}
                        >
                            All
                        </button>
                        <button
                            className={`px-2.5 py-1 border-l ${view === 'OWNED_ME' ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'}`}
                            onClick={() => setView('OWNED_ME')}
                            disabled={currentUserId == null}
                            title="Owned by me"
                        >
                            Mine
                        </button>
                        <button
                            className={`px-2.5 py-1 border-l ${view === 'CREATED_ME' ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'}`}
                            onClick={() => setView('CREATED_ME')}
                            disabled={currentUserId == null}
                            title="Created by me"
                        >
                            Created by me
                        </button>
                    </div>

                    <label className="ml-2 inline-flex items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            className="accent-gray-900"
                            checked={includeClosed}
                            onChange={(e) => setIncludeClosed(e.target.checked)}
                        />
                        <span>Show Closed</span>
                    </label>

                    {filterCategory && (
                        <span className="ml-2 inline-flex items-center gap-2 text-xs rounded-full border px-2 py-0.5">
                            Category: <b>{filterCategory}</b>
                            <button className="opacity-70 hover:opacity-100" onClick={() => setFilterCategory(null)}>
                                ×
                            </button>
                        </span>
                    )}

                    {(filterCategory || includeClosed) && (
                        <button className="ml-2 text-xs underline" onClick={clearFilters}>
                            Clear filters
                        </button>
                    )}

                    {/* 정렬 UI */}
                    <div className="ml-2 flex items-center gap-2">
                        <div className="text-xs text-gray-500">정렬:</div>
                        <select
                            className="text-xs rounded border px-2 py-1"
                            value={sortKey}
                            onChange={(e) => setSortKey(e.target.value as SortKey)}
                        >
                            <option value="createdAt">생성일</option>
                            <option value="updatedAt">수정일</option>
                            <option value="status">상태</option>
                            <option value="dueDate">Due Date</option>
                            <option value="rag">RAG</option>
                            <option value="score">Score</option>
                            <option value="category">카테고리</option>
                            <option value="title">Title</option>
                            <option value="owner">책임자</option>
                            <option value="createdBy">생성자</option>
                            <option value="id">ID</option>
                        </select>
                        <button
                            className="text-xs rounded border px-2 py-1 hover:bg-gray-50"
                            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                            title="정렬 방향 전환"
                        >
                            {sortDir === 'asc' ? '▲ asc' : '▼ desc'}
                        </button>
                    </div>

                    <span className="ml-2 text-xs text-gray-500">총 {sorted.length}건</span>
                </div>

                <div>
                    <button
                        className="text-xs rounded-lg border px-2.5 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-50"
                        onClick={openNew}
                        disabled={!canEdit}
                    >
                        + New Risk
                    </button>
                </div>
            </div>

            {/* 리스트 테이블: 읽기 전용(행 클릭 → 상세 모달) */}
            <div className="rounded-2xl border bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-[1200px] w-full text-sm">
                        <thead>
                            <tr className="text-left text-gray-500">
                                {/* <th className="px-3 py-3 //w-16">ID</th> */}
                                <th className="px-3 py-3 //w-16">SEQ.</th>
                                <th className="px-3 py-3 //w-[280px] min-w-[110px]">
                                    <div>Title / Category</div>
                                    <div className="text-xs text-gray-500 font-medium">제목 / 카테고리</div>
                                </th>
                                <th className="px-3 py-3 //w-[320px] //w-[12%] max-w-[240px]">
                                    <div>Description</div>
                                    <div className="text-xs text-gray-500 font-medium">상세 설명</div>
                                </th>
                                <th className="px-3 py-3 //w-20">
                                    <div>Score</div>
                                    <div className="text-xs text-gray-500 font-medium">점수</div>
                                </th>
                                <th className="px-3 py-3 //w-20">
                                    <div>RAG</div>
                                    <div className="text-xs text-gray-500 font-medium">위험도</div>
                                </th>
                                <th className="px-3 py-3 //w-32">
                                    <div>Created At</div>
                                    <div className="text-xs text-gray-500 font-medium">생성일</div>
                                </th>
                                <th className="px-3 py-3 //w-28">
                                    <div>Created By</div>
                                    <div className="text-xs text-gray-500 font-medium">생성자</div>
                                </th>
                                <th className="px-3 py-3 //w-28">
                                    <div>Owned By</div>
                                    <div className="text-xs text-gray-500 font-medium">책임자</div>
                                </th>
                                <th className="px-3 py-3 //w-32">
                                    <div>Due Date</div>
                                    <div className="text-xs text-gray-500 font-medium">해소 목표일</div>
                                </th>
                                <th className="px-3 py-3 //w-[260px] //w-[15%] max-w-[240px]">
                                    <div>Mitigation Plan</div>
                                    <div className="text-xs text-gray-500 font-medium">리스크 완화계획</div>
                                </th>
                                <th className="px-3 py-3 //w-[240px] //w-[15%] max-w-[240px]">
                                    <div>Contingency Plan</div>
                                    <div className="text-xs text-gray-500 font-medium">발생시 대응계획</div>
                                </th>
                                <th className="px-3 py-3 //w-[260px] //w-[10%] max-w-[240px]">
                                    <div>Weekly Memo</div>
                                    <div className="text-xs text-gray-500 font-medium">주간 메모</div>
                                </th>
                                <th className="px-3 py-3 //w-32">
                                    <div>Next Review</div>
                                    <div className="text-xs text-gray-500 font-medium">차회 점검일</div>
                                </th>
                                <th className="px-3 py-3 //w-32">
                                    <div>Updated At</div>
                                    <div className="text-xs text-gray-500 font-medium">내용 갱신일</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((r, idx) => {
                                const isClosed = (r.status ?? 'OPEN') === 'CLOSED';
                                return (
                                    <tr
                                        key={r.id}
                                        className="border-t hover:bg-gray-50 cursor-pointer"
                                        onClick={() => openEdit(r)}
                                        title="클릭하여 상세 보기/수정"
                                    >
                                        {/* <td className="px-3 py-2 text-gray-500">{r.id}</td> */}
                                        <td className="px-3 py-2 text-gray-500">{idx + 1}</td>

                                        <td className="px-3 py-2">
                                            <div className="font-medium">{r.title}</div>
                                            <div className="text-[11px] text-gray-500">{r.category || '—'}</div>
                                        </td>

                                        <td className="px-3 py-2">
                                            <div className="line-clamp-2 whitespace-pre-wrap">
                                                {r.description || '—'}
                                            </div>
                                        </td>

                                        <td className="px-3 py-2 text-right">{r.score ?? '—'}</td>

                                        <td className="px-3 py-2">
                                            <span
                                                className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs ${ragPill(
                                                    r.rag,
                                                )}`}
                                            >
                                                {r.rag ?? '—'}
                                            </span>
                                            {isClosed && (
                                                <span className="ml-2 text-[11px\ px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                                    Closed
                                                </span>
                                            )}
                                        </td>

                                        {/* <td className="px-3 py-2">{r.createdAt?.slice(0, 10) || '—'}</td> */}
                                        <td className="px-3 py-2">{toProjectDateOnly(r.createdAt, zone) || '—'}</td>
                                        <td className="px-3 py-2">{renderUserCell(r.createdByUserId)}</td>
                                        <td className="px-3 py-2">{renderUserCell(r.ownerUserId)}</td>
                                        {/* <td className="px-3 py-2">{r.dueDate?.slice(0, 10) || '-'}</td> */}
                                        <td className="px-3 py-2">{r.dueDate || '-'}</td>

                                        <td className="px-3 py-2">
                                            <div className="line-clamp-2 whitespace-pre-wrap">
                                                {r.mitigationPlan || '—'}
                                            </div>
                                        </td>

                                        <td className="px-3 py-2">
                                            <div className="line-clamp-2 whitespace-pre-wrap">
                                                {r.contingencyPlan || '—'}
                                            </div>
                                        </td>

                                        <td className="px-3 py-2">
                                            <div className="line-clamp-2 whitespace-pre-wrap">
                                                {r.weeklyNote || '—'}
                                            </div>
                                        </td>

                                        {/* <td className="px-3 py-2">{r.nextReviewAt?.slice(0, 10) || '—'}</td> */}
                                        <td className="px-3 py-2">{r.nextReviewAt || '—'}</td>
                                        {/* <td className="px-3 py-2">{r.updatedAt?.slice(0, 10) || '—'}</td> */}
                                        <td className="px-3 py-2">{toProjectDateOnly(r.updatedAt, zone) || '—'}</td>
                                    </tr>
                                );
                            })}

                            {sorted.length === 0 && (
                                <tr>
                                    <td colSpan={13} className="px-3 py-6 text-center text-gray-500">
                                        등록된 리스크가 없습니다.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <RiskEditorModal
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                projectId={projectId}
                risk={editing}
                canEdit={canEdit}
                currentUserId={currentUserId}
                userNameMap={userNameMap}
                onCreate={onCreate}
                onUpdate={onUpdate}
                onDelete={onDelete}
                onSaved={() => reloadRisks()}
                onDeleted={() => reloadRisks()}
            />
        </div>
    );
}
